import { useEffect, useMemo, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Snowflake, Loader2 } from 'lucide-react';
import api from '../../lib/api';

const when = v => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const PAGE = 100;
const sel = 'text-xs border border-slate-200 rounded-md px-2 h-8 bg-white';

const CallsDrawer = ({ lead, onClose }) => {
  const [calls, setCalls] = useState(null);
  useEffect(() => { if (lead) { setCalls(null); api.get(`/callback-tracker/lead/${lead.id}/calls`).then(r => setCalls(r.data)).catch(() => setCalls([])); } }, [lead]);
  return (
    <Sheet open={!!lead} onOpenChange={o => !o && onClose()}>
      <SheetContent className="w-[480px] sm:max-w-[480px] overflow-y-auto" data-testid="cbt-calls-drawer">
        <SheetHeader><SheetTitle>{lead?.name}</SheetTitle></SheetHeader>
        {lead && <p className="text-xs text-slate-500 mt-1">Owner {lead.owner} · in Call Back {lead.since_exact ? 'since' : 'on or before'} {lead.since} ·{' '}
          <a className="text-blue-700 hover:underline" href={`https://crm.zoho.com/crm/tab/Leads/${lead.id}`} target="_blank" rel="noreferrer">Open in Zoho CRM</a></p>}
        {!calls ? <Loader2 className="w-5 h-5 animate-spin mt-6 text-slate-400" /> : (
          <table className="w-full text-xs mt-4">
            <thead className="text-[10px] uppercase tracking-wider text-slate-400"><tr><th className="text-left py-1">When</th><th className="text-left">By</th><th className="text-left">Type</th><th className="text-right">Duration</th></tr></thead>
            <tbody>
              {calls.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-slate-400">No calls logged in the last 90 days.</td></tr>}
              {calls.map(c => (
                <tr key={c.id} className="border-t border-slate-100"><td className="py-1.5">{when(c.start)}</td><td>{c.owner}</td><td>{c.type}</td>
                  <td className={`text-right ${c.duration ? 'text-emerald-700' : 'text-slate-400'}`}>{c.duration ? `${Math.floor(c.duration / 60)}m ${c.duration % 60}s` : 'not connected'}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </SheetContent>
    </Sheet>
  );
};

export const CbtLeads = ({ data, coldOnly }) => {
  const [f, setF] = useState({ owner: '', show: coldOnly ? 'cold' : 'all', q: '' });
  const [limit, setLimit] = useState(PAGE);
  const [open, setOpen] = useState(null);
  const owners = useMemo(() => [...new Set(data.leads.map(x => x.owner))].sort(), [data.leads]);
  const rows = useMemo(() => data.leads.filter(x => (!f.owner || x.owner === f.owner)
    && (f.show === 'all' || (f.show === 'cold' && x.cold) || (f.show === 'never' && !x.calls) || (f.show === 'active' && x.calls > 0 && !x.cold))
    && (!f.q || x.name.toLowerCase().includes(f.q.toLowerCase()))), [data.leads, f]);
  const upd = p => { setF(v => ({ ...v, ...p })); setLimit(PAGE); };
  return (
    <Card className="p-4" data-testid="cbt-leads-table">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="text-sm font-bold text-slate-900 mr-2">Call Back leads <span className="text-slate-400 font-normal">({rows.length})</span></h3>
        <select className={sel} value={f.owner} onChange={e => upd({ owner: e.target.value })} data-testid="cbt-filter-owner">
          <option value="">All owners</option>{owners.map(o => <option key={o} value={o}>{o}</option>)}</select>
        <select className={sel} value={f.show} onChange={e => upd({ show: e.target.value })} data-testid="cbt-filter-show">
          <option value="all">All leads</option><option value="cold">Going cold</option><option value="never">Never called</option><option value="active">Followed up</option></select>
        <Input value={f.q} onChange={e => upd({ q: e.target.value })} placeholder="Search lead name" className="h-8 w-48 text-xs" data-testid="cbt-filter-search" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-slate-400"><tr>
            <th className="text-left py-1">Lead</th><th className="text-left">Owner</th><th className="text-left">In Call Back since</th><th className="text-right">Calls</th>
            <th className="text-right">Connected</th><th className="text-left pl-3">Last call</th><th className="text-left">Next scheduled</th><th className="text-right">Idle days</th><th className="text-left pl-3">Called by</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={9} className="text-center text-slate-400 py-8">No leads match.</td></tr>}
            {rows.slice(0, limit).map(x => (
              <tr key={x.id} onClick={() => setOpen(x)} className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${x.cold ? 'bg-sky-50/40' : ''}`} data-testid={`cbt-lead-${x.id}`}>
                <td className="py-1.5 font-medium text-slate-800">{x.cold && <Snowflake className="w-3 h-3 inline mr-1 text-sky-600" />}{x.name || x.id}</td>
                <td>{x.owner}</td><td className="text-slate-500">{x.since_exact ? '' : '≤ '}{x.since}</td>
                <td className="text-right font-bold">{x.calls}</td><td className="text-right text-emerald-700">{x.connected}</td>
                <td className="pl-3">{x.last_call_at ? `${when(x.last_call_at)} · ${x.last_call_by}` : <span className="text-rose-600">never</span>}</td>
                <td>{x.next_call_at ? when(x.next_call_at) : '—'}</td>
                <td className={`text-right ${x.cold ? 'text-sky-700 font-semibold' : ''}`}>{x.idle_days}</td>
                <td className="pl-3 text-slate-500">{Object.entries(x.by_user).map(([u, n]) => `${u} ${n}`).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > limit && <div className="text-center mt-3"><Button size="sm" variant="outline" onClick={() => setLimit(l => l + PAGE)} data-testid="cbt-leads-more">Show more ({rows.length - limit} left)</Button></div>}
      <CallsDrawer lead={open} onClose={() => setOpen(null)} />
    </Card>
  );
};
